/**
 * TradeStack - Invoices List Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Documents.css';

function Invoices() {
  const { formatCurrency, formatDate } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    outstanding: 0,
    overdue: 0,
    paidThisMonth: 0
  });

  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.getInvoices({
        status: statusFilter,
        limit: 20,
        offset: (page - 1) * 20
      });
      setInvoices(response.data.invoices || []);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (err) {
      setError('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const statuses = [
    { id: 'draft', label: 'Draft' },
    { id: 'sent', label: 'Sent' },
    { id: 'viewed', label: 'Viewed' },
    { id: 'partial', label: 'Partial' },
    { id: 'paid', label: 'Paid' },
    { id: 'overdue', label: 'Overdue' }
  ];

  return (
    <div className="documents-page">
      <div className="page-header">
        <h1>Invoices</h1>
        <Link to="/invoices/new" className="btn btn-primary">+ New Invoice</Link>
      </div>

      {/* Stats */}
      <div className="invoice-stats">
        <div className="stat-card warning">
          <span className="stat-label">Outstanding</span>
          <span className="stat-value">{formatCurrency(stats.outstanding)}</span>
        </div>
        <div className="stat-card danger">
          <span className="stat-label">Overdue</span>
          <span className="stat-value">{formatCurrency(stats.overdue)}</span>
        </div>
        <div className="stat-card success">
          <span className="stat-label">Paid This Month</span>
          <span className="stat-value">{formatCurrency(stats.paidThisMonth)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="status-filters">
          <button
            className={`filter-btn ${!statusFilter ? 'active' : ''}`}
            onClick={() => handleStatusFilter('')}
          >
            All
          </button>
          {statuses.map(status => (
            <button
              key={status.id}
              className={`filter-btn ${statusFilter === status.id ? 'active' : ''}`}
              onClick={() => handleStatusFilter(status.id)}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="loading-state">Loading invoices...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : invoices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>No invoices found</h3>
          <p>Create your first invoice to get paid</p>
          <Link to="/invoices/new" className="btn btn-primary">Create Invoice</Link>
        </div>
      ) : (
        <div className="documents-table">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Job</th>
                <th>Date</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(invoice => (
                <tr key={invoice.id} className={invoice.status === 'overdue' ? 'overdue-row' : ''}>
                  <td>
                    <Link to={`/invoices/${invoice.id}`} className="doc-link">
                      {invoice.invoice_number}
                    </Link>
                  </td>
                  <td>{invoice.customer_name || '-'}</td>
                  <td>{invoice.job_title || '-'}</td>
                  <td>{formatDate(invoice.invoice_date)}</td>
                  <td>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</td>
                  <td className="amount">{formatCurrency(invoice.total)}</td>
                  <td className="amount balance">
                    {formatCurrency(invoice.balance_due)}
                  </td>
                  <td>
                    <span className={`status-badge status-${invoice.status}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <Link to={`/invoices/${invoice.id}`} className="action-btn">View</Link>
                      {invoice.balance_due > 0 && (
                        <Link to={`/invoices/${invoice.id}/payment`} className="action-btn primary">
                          Record Payment
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Invoices;
